import React, { useState, useEffect } from "react";
import TwoFactorService from "../../services/twoFactorService";

const TwoFactorModal = () => {
    const [viewState, setViewState] = useState("STATUS"); // STATUS, SETUP, SUCCESS, DISABLE
    const [isEnabled, setIsEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Setup state
    const [secretKey, setSecretKey] = useState("");
    const [otpAuthUri, setOtpAuthUri] = useState("");
    const [verifyCode, setVerifyCode] = useState("");
    const [recoveryCodes, setRecoveryCodes] = useState([]);

    // Disable state
    const [disablePassword, setDisablePassword] = useState("");
    const [disableCode, setDisableCode] = useState("");

    // Fetch status whenever modal is shown or resets
    const loadStatus = async () => {
        try {
            setLoading(true);
            setErrorMessage("");
            const res = await TwoFactorService.getStatus();
            if (res?.data?.Status) {
                setIsEnabled(res.data.IsEnabled);
            }
        } catch (err) {
            // Ignored if user not logged in
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
    }, []);

    const resetModal = () => {
        setViewState("STATUS");
        setVerifyCode("");
        setDisablePassword("");
        setDisableCode("");
        setErrorMessage("");
        setSuccessMessage("");
        loadStatus();
    };

    const handleStartSetup = async () => {
        try {
            setLoading(true);
            setErrorMessage("");
            const res = await TwoFactorService.setupInit();
            if (res?.data?.Status) {
                setSecretKey(res.data.SecretKey);
                setOtpAuthUri(res.data.OtpAuthUri);
                setViewState("SETUP");
            } else {
                setErrorMessage(res?.data?.Message || "Failed to start 2FA setup.");
            }
        } catch (err) {
            setErrorMessage("Failed to connect to server. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmSetup = async () => {
        if (!verifyCode || verifyCode.trim().length !== 6) {
            setErrorMessage("Please enter a valid 6-digit code.");
            return;
        }

        try {
            setLoading(true);
            setErrorMessage("");
            const res = await TwoFactorService.setupConfirm(verifyCode.trim());
            if (res?.data?.Status) {
                setRecoveryCodes(res.data.RecoveryCodes || []);
                setIsEnabled(true);
                setViewState("SUCCESS");
            } else {
                setErrorMessage(res?.data?.Message || "Invalid 6-digit code.");
            }
        } catch (err) {
            setErrorMessage("Verification failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDisable = async () => {
        if (!disablePassword || !disableCode) {
            setErrorMessage("Password and 6-digit code are both required.");
            return;
        }

        try {
            setLoading(true);
            setErrorMessage("");
            const res = await TwoFactorService.disable(disablePassword, disableCode.trim());
            if (res?.data?.Status) {
                setIsEnabled(false);
                setSuccessMessage("Two-Factor Authentication has been disabled.");
                setViewState("STATUS");
            } else {
                setErrorMessage(res?.data?.Message || "Failed to disable 2FA.");
            }
        } catch (err) {
            setErrorMessage("Server error while disabling 2FA.");
        } finally {
            setLoading(false);
        }
    };

    const copyRecoveryCodes = () => {
        if (recoveryCodes && recoveryCodes.length > 0) {
            navigator.clipboard.writeText(recoveryCodes.join("\n"));
            alert("Recovery codes copied to clipboard!");
        }
    };

    const qrCodeUrl = otpAuthUri 
        ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(otpAuthUri)}`
        : "";

    return (
        <div 
            className="modal fade lg" 
            id="twoFactorModal" 
            tabIndex="-1" 
            aria-labelledby="twoFactorModalLabel" 
            aria-hidden="true" 
            style={{ marginTop: "50px" }}
        >
            <div className="modal-dialog" style={{ maxWidth: "420px", width: "95%", margin: "0.5rem auto" }}>
                    <div className="modal-content">
                    <div className="modal-header plus-icon-modal-header">
                        <h4 className="modal-title">Two-Factor Authentication</h4>
                        <button 
                            type="button" 
                            className="close" 
                            data-bs-dismiss="modal" 
                            style={{ color: "#fff", opacity: "inherit" }}
                            onClick={resetModal}
                        >
                            x
                        </button>
                    </div>

                    <div className="modal-body pe-3 ps-3 pb-3 pt-3" style={{ border: "1px solid black" }}>
                        {errorMessage && (
                            <div className="alert alert-danger p-2 mb-3" style={{ fontSize: "12px" }}>
                                {errorMessage}
                            </div>
                        )}
                        {successMessage && (
                            <div className="alert alert-success p-2 mb-3" style={{ fontSize: "12px" }}>
                                {successMessage}
                            </div>
                        )}

                        {/* VIEW 1: STATUS */}
                        {viewState === "STATUS" && (
                            <div>
                                <div className="d-flex align-items-center justify-content-between mb-3 p-2" style={{ background: "#f8f9fa", borderRadius: "4px" }}>
                                    <span style={{ fontSize: "14px", fontWeight: "600" }}>Status:</span>
                                    {isEnabled ? (
                                        <span className="badge bg-success" style={{ fontSize: "12px" }}>
                                            Enabled 🟢
                                        </span>
                                    ) : (
                                        <span className="badge bg-secondary" style={{ fontSize: "12px" }}>
                                            Disabled ⚪
                                        </span>
                                    )}
                                </div>

                                <p style={{ fontSize: "13px", color: "#555" }}>
                                    {isEnabled
                                        ? "Your account is protected with Two-Factor Authentication. You will need your authenticator app code to log in."
                                        : "Protect your account from unauthorized access by requiring an authenticator code (Google Authenticator) during login."}
                                </p>

                                <div className="text-center mt-3">
                                    {isEnabled ? (
                                        <button 
                                            type="button" 
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => { setErrorMessage(""); setViewState("DISABLE"); }}
                                            disabled={loading}
                                        >
                                            Turn Off 2FA
                                        </button>
                                    ) : (
                                        <button 
                                            type="button" 
                                            className="btn btn-warning modal-btn" 
                                            style={{ fontSize: "13px", padding: "8px 24px" }}
                                            onClick={handleStartSetup}
                                            disabled={loading}
                                        >
                                            {loading ? "Loading..." : "Turn On 2FA"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* VIEW 2: SETUP (QR CODE + 6-DIGIT CODE) */}
                        {viewState === "SETUP" && (
                            <div>
                                <div className="text-center mb-2">
                                    <p style={{ fontSize: "12px", margin: "0", color: "#444" }}>
                                        1. Scan with <strong>Google Authenticator</strong> or <strong>Microsoft Authenticator</strong>:
                                    </p>
                                    {qrCodeUrl && (
                                        <div className="my-2">
                                            <img 
                                                src={qrCodeUrl} 
                                                alt="2FA QR Code" 
                                                style={{ width: "160px", height: "160px", border: "1px solid #ddd", borderRadius: "4px" }}
                                            />
                                        </div>
                                    )}
                                    <div style={{ fontSize: "11px", color: "#777" }}>
                                        Manual Key: <code style={{ color: "#d9534f" }}>{secretKey}</code>
                                    </div>
                                </div>

                                <div className="mt-3">
                                    <label htmlFor="verifyCode" className="form-label edit-user-input-label mb-1">
                                        2. Enter 6-Digit Code from App:
                                    </label>
                                    <input 
                                        type="text" 
                                        maxLength="6"
                                        className="form-control report-details-input text-center w-100" 
                                        placeholder="000000"
                                        style={{ fontSize: "18px", letterSpacing: "4px" }}
                                        value={verifyCode}
                                        onChange={(e) => setVerifyCode(e.target.value.replace(/[^0-9]/g, ""))}
                                    />
                                </div>

                                <div className="d-flex justify-content-between mt-3">
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setViewState("STATUS")}
                                        disabled={loading}
                                    >
                                        Back
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-warning modal-btn btn-sm"
                                        onClick={handleConfirmSetup}
                                        disabled={loading || verifyCode.length !== 6}
                                    >
                                        {loading ? "Verifying..." : "Verify & Activate"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* VIEW 3: SUCCESS & BACKUP RECOVERY CODES */}
                        {viewState === "SUCCESS" && (
                            <div>
                                <div className="text-center mb-2">
                                    <span style={{ fontSize: "28px" }}>🎉</span>
                                    <h5 style={{ fontSize: "16px", marginTop: "4px" }}>2FA Activated!</h5>
                                    <p style={{ fontSize: "12px", color: "#666" }}>
                                        Save these <strong>emergency recovery codes</strong>. Each can be used once if you lose your phone.
                                    </p>
                                </div>

                                <div 
                                    className="p-2 mb-3" 
                                    style={{ 
                                        background: "#f1f3f5", 
                                        borderRadius: "4px", 
                                        maxHeight: "120px", 
                                        overflowY: "auto",
                                        fontFamily: "monospace",
                                        fontSize: "13px",
                                        textAlign: "center"
                                    }}
                                >
                                    {recoveryCodes.map((code, idx) => (
                                        <div key={idx} style={{ padding: "2px 0" }}>{code}</div>
                                    ))}
                                </div>

                                <div className="d-flex justify-content-between">
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-dark btn-sm"
                                        onClick={copyRecoveryCodes}
                                    >
                                        Copy Codes
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-warning modal-btn btn-sm"
                                        data-bs-dismiss="modal"
                                        onClick={resetModal}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* VIEW 4: DISABLE 2FA */}
                        {viewState === "DISABLE" && (
                            <div>
                                <p style={{ fontSize: "12px", color: "#d9534f" }}>
                                    To turn off 2FA, enter your current password and a 6-digit code from your authenticator app.
                                </p>
                                <div className="mb-2">
                                    <label className="form-label edit-user-input-label mb-0">Current Password</label>
                                    <input 
                                        type="password" 
                                        className="form-control report-details-input w-100" 
                                        placeholder="Password"
                                        value={disablePassword}
                                        onChange={(e) => setDisablePassword(e.target.value)}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label edit-user-input-label mb-0">6-Digit Code</label>
                                    <input 
                                        type="text" 
                                        maxLength="6"
                                        className="form-control report-details-input w-100 text-center" 
                                        placeholder="000000"
                                        style={{ fontSize: "16px", letterSpacing: "2px" }}
                                        value={disableCode}
                                        onChange={(e) => setDisableCode(e.target.value.replace(/[^0-9]/g, ""))}
                                    />
                                </div>

                                <div className="d-flex justify-content-between">
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setViewState("STATUS")}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-danger btn-sm"
                                        onClick={handleDisable}
                                        disabled={loading || !disablePassword || disableCode.length !== 6}
                                    >
                                        {loading ? "Disabling..." : "Confirm Disable"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TwoFactorModal;
